import axios from 'axios'

/**
 * Configuration holder for the vasion file provider.
 * Must be initialized via `configureVasionProvider` before files can be uploaded.
 */
let providerConfig = {
  getApiToken: () => '',
  getBaseURL: () => axios.defaults.baseURL || '',
}

/**
 * Configure the vasion file provider with access to auth and upload target.
 * Call this before forms are rendered.
 *
 * @param {{ getApiToken: () => string, getBaseURL?: () => string }} config
 */
function configureVasionProvider(config) {
  if (config.getApiToken) providerConfig.getApiToken = config.getApiToken
  if (config.getBaseURL) providerConfig.getBaseURL = config.getBaseURL
}

const vasionFileProvider = function vasionFileProvider() {
  return {
    title: 'Vasion',
    name: 'vasion',
    uploadFile(file, fileName, dir, progressCallback) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        formData.append('file', file, fileName || file.name)

        const token = providerConfig.getApiToken()
        const baseURL = providerConfig.getBaseURL()

        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${baseURL}v1/Upload/UploadTempFileMultipart`)
        xhr.setRequestHeader('Authorization', `X-MVApiAuth ${token}`)
        xhr.setRequestHeader('Accept-Language', 'en-US')

        if (progressCallback) {
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              progressCallback(event.loaded / event.total)
            }
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            let response
            try {
              response = JSON.parse(xhr.responseText)
            } catch (e) {
              return reject(new Error('Invalid response from upload server'))
            }
            const documentId = response?.Value
            if (!documentId || documentId === '') {
              return reject(new Error('Upload failed: no document ID returned'))
            }
            resolve({
              storage: 'vasion',
              name: fileName || file.name,
              originalName: file.name,
              size: file.size,
              type: file.type,
              documentId: documentId,
              url: `v1/Document/GetDocumentStream/${documentId}`,
            })
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        }

        xhr.onerror = () => reject(new Error('Network error during file upload'))
        xhr.onabort = () => reject(new Error('File upload aborted'))

        xhr.send(formData)
      })
    },
    downloadFile(file) {
      return Promise.resolve(file)
    },
    deleteFile(fileInfo) {
      return Promise.resolve(`File ${fileInfo.name || fileInfo.originalName || 'upload'} deleted`)
    },
  }
}

vasionFileProvider.title = 'Vasion'

/**
 * Extract vasion file references from submission data.
 * Files uploaded via the vasion provider already have a documentId on the server,
 * so no base64 conversion is needed — only the reference is returned.
 */
function extractVasionFiles(submissionData) {
  const uploadedDocuments = []
  const cleanedData = {}

  for (const key of Object.keys(submissionData)) {
    const value = submissionData[key]
    if (Array.isArray(value) && value.length > 0 && value[0]?.storage === 'vasion') {
      const cleanedFiles = []
      for (const fileInfo of value) {
        if (fileInfo.documentId) {
          uploadedDocuments.push({
            ID: parseInt(fileInfo.documentId, 10),
            OriginalFilename: fileInfo.originalName || fileInfo.name || 'upload',
            size: fileInfo.size || 0,
            url: fileInfo.url || '',
            fieldKey: key,
          })
        }
        cleanedFiles.push({
          storage: fileInfo.storage,
          name: fileInfo.name,
          originalName: fileInfo.originalName,
          size: fileInfo.size,
          type: fileInfo.type,
          documentId: fileInfo.documentId,
          url: fileInfo.url || '',
        })
      }
      cleanedData[key] = cleanedFiles
    } else {
      cleanedData[key] = value
    }
  }

  return { cleanedData, uploadedDocuments }
}

export {
  vasionFileProvider,
  configureVasionProvider,
  extractVasionFiles,
}
